import { EncryptedStorage } from "./encrypted-storage"
import { AesCryptor } from "./aes-cryptor"
import { querySelectorNonNull } from "../utils"

export interface FormState {
    encryptionKey: string,
    fieldsToStore: string[],
    disableSetFormValues?: boolean
}

export class FormStorage {
    readonly form: HTMLFormElement
    readonly formState: FormState
    readonly storage: EncryptedStorage

    constructor(
        documentObject: Document,
        private readonly storageKey: string,
        formSelector: string = ".fti-cr-js-form",
    ) {
        this.form = querySelectorNonNull(documentObject, formSelector)
        this.formState = JSON.parse(this.form.getAttribute("data-fti-form-state")!!) as FormState
        this.storage = new EncryptedStorage(sessionStorage, new AesCryptor(this.formState.encryptionKey))
        this.form.addEventListener("submit", () => {
            this.storage.setItem(storageKey, JSON.stringify(this.getFormValues()))
                .catch((error: unknown) => {
                    console.error("Failed to store form values:", error)
                })
        })
    }

    async setFormValues() {
        if (this.formState.disableSetFormValues) {
            return
        }
        const userInput = await this.getValuesFromStorage(this.storageKey)
        const formValues = this.getFormValuesToSet(userInput)
        /*                                                    */
        this.formState.fieldsToStore.forEach((field) => {
            const valueToSet = formValues[field]
            if (valueToSet !== undefined) {
                const inputElements = this.form.querySelectorAll<HTMLInputElement | HTMLSelectElement>(`[name='${field}']`)
                inputElements.forEach((inputElement) => {
                    this.setValue(inputElement, valueToSet)
                })
            }
        })
    }

    private async getValuesFromStorage(storageKey: string): Promise<Record<string, string> | null> {
        const storageValue = await this.storage.getItem(storageKey)
        if (storageValue) {
            return JSON.parse(storageValue) as Record<string, string>
        }
        return null
    }

    private getFormValues(): Record<string, string> {
        const formValues: Record<string, string> = {}
        this.formState.fieldsToStore.forEach((field) => {
            const inputElements = this.form.querySelectorAll<HTMLInputElement>(`[name='${field}']`)
            inputElements.forEach((inputElement) => {
                this.getValue(inputElement, formValues, field)
            })
        })
        return formValues
    }

    private setValue(inputElement: HTMLInputElement | HTMLSelectElement, valueToSet: string) {
        if (inputElement.type === "checkbox" || inputElement.type === "radio") {
            inputElement.checked = inputElement.value === valueToSet
        } else if (inputElement.type === "select-one") {
            const selectElement = inputElement as HTMLSelectElement
            const options = selectElement.options
            let optionFound = false
            for (let i = 0; i < options.length; i++) {
                const option = options[i]
                if (option.value === valueToSet) {
                    selectElement.selectedIndex = i
                    optionFound = true
                    break
                }
            }
            if (optionFound && selectElement.classList.contains("pl_dropdown__element")) {
                selectElement.setAttribute("data-value", valueToSet)
            }
        } else {
            inputElement.value = valueToSet
            inputElement.setAttribute("value", valueToSet)
        }
    }

    private getValue(inputElement: HTMLInputElement, formValues: Record<string, string>, field: string) {
        if (inputElement.type === "checkbox" || inputElement.type === "radio") {
            if (inputElement.checked) {
                formValues[field] = inputElement.value
            }
        } else {
            formValues[field] = inputElement.value
        }
    }

    private getFormValuesToSet(storedValues: Record<string, string> | null): Record<string, string> {
        if (storedValues) {
            return storedValues
        } else {
            return {}
        }
    }
}
